import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Dimensions,
    FlatList,
    ListRenderItemInfo,
    Platform,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { SvgProps } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import debounce from 'lodash.debounce';

import SkeletonChannelsLoadingMobile from 'assets/images/animation/collection_channels_loading_mobile.svg';
import SkeletonChannelsLoadingTablet from 'assets/images/animation/collection_channels_loading_tablet.svg';
import SkeletonChannelsLoadingWeb from 'assets/images/animation/collection_channels_loading_web.svg';
import SkeletonMixedLoadingMobile from 'assets/images/animation/collection_mixed_loading_mobile.svg';
import SkeletonMixedLoadingTablet from 'assets/images/animation/collection_mixed_loading_tablet.svg';
import SkeletonMixedLoadingWeb from 'assets/images/animation/collection_mixed_loading_web.svg';
import SkeletonNowNextLoadingMobile from 'assets/images/animation/collection_now_next_loading_mobile.svg';
import SkeletonNowNextLoadingTablet from 'assets/images/animation/collection_now_next_loading_tablet.svg';
import SkeletonNowNextLoadingWeb from 'assets/images/animation/collection_now_next_loading_web.svg';

import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { StreamTypes } from 'store/player/actions.types';
import { selectChannels } from 'store/programs/selectors';
import {
    MdsCallKeys,
    MixedItem,
    RBChannel,
    RBParsedRail,
    RBProgram,
    SearchProgram,
} from 'store/railsBuilder/reducer.types';
import {
    selectSearchRailById,
    selectTemplateviewRailById,
} from 'store/railsBuilder/selectors';
import { RecordingItem } from 'store/recordings/reducer.types';
import { endSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import useCollectionDataManager from 'services/data-manager/hooks/useCollectionDataMananger';
import parseKidsChannels from 'services/data-manager/parse-kids-channels';
import {
    ContentType,
    EventItemWithServiceInfo,
    NowNextData,
} from 'services/data-manager/types';
import useGetDeepLinkTriggeredParams from 'services/uav/useGetDeepLinkTriggeredParams';
import useGetRailSelectionParams from 'services/uav/useGetRailSelectionParams';
import useGetRailViewParams from 'services/uav/useGetRailViewParams';
import useThirdPartyAppStartParams from 'services/uav/useGetThirdPartyAppStartParams';
import { logUavAnalytics } from 'services/uav/utils';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import useNavigateBackOnHardwareBackPress from 'utils/hooks/useNavigateBackOnHardwareBackPress';
import usePrevious from 'utils/hooks/usePrevious';
import useScreenFocused from 'utils/hooks/useScreenFocused';
import useViewableItems from 'utils/hooks/useViewableItems';
import {
    isPhone,
    isTablet,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import {
    DIALOG_CANCEL_BUTTON,
    DIALOG_ERR_GENERIC_BODY,
    DIALOG_ERR_GENERIC_TITLE,
    DIALOG_TRY_AGAIN_BUTTON,
} from 'screens/Collection/constants';
import useChannelsSearch from 'screens/Search/hooks/useChannelsSearch';
import { SearchType } from 'screens/Search/types';

import ErrorDialog from 'components/ErrorDialog';
import useAccessibilityFocus from 'components/Gallery/hooks/useAccessibilityFocus';
import { RefreshControl } from 'components/RefreshControl';
import RibbonItem from 'components/Ribbon/RibonItem';
import {
    DeepLinkTriggered,
    ThirdPartyAppStartItem,
} from 'components/Ribbon/types';
import SkeletonLoading from 'components/SkeletonLoading';
import Text from 'components/Text';

import styles, {
    emptyItemStyleByContentType,
    listContainerStyle,
} from './style';
import { CollectionItemsList, ColumnsCollectionItemsList } from './types';
import { EmptyCollectionItem } from './utils/createEmptyItem';
import isEmptyItem from './utils/isEmptyItem';
import prepareCollectionList from './utils/prepareCollectionList';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.COLLECTION>;
    route: RouteProp<RouteNavigationParams, SCREEN.COLLECTION>;
};
const SkeletonToMetadataType: Partial<Record<ContentType, React.FC<SvgProps>>> =
    {
        [ContentType.ChannelItem]: Platform.select({
            web: SkeletonChannelsLoadingWeb,
            default: isTablet
                ? SkeletonChannelsLoadingTablet
                : SkeletonChannelsLoadingMobile,
        }),
        [ContentType.NowNext]: Platform.select({
            web: SkeletonNowNextLoadingWeb,
            default: isTablet
                ? SkeletonNowNextLoadingTablet
                : SkeletonNowNextLoadingMobile,
        }),
        [ContentType.Catchup]: Platform.select({
            web: SkeletonMixedLoadingWeb,
            default: isTablet
                ? SkeletonMixedLoadingTablet
                : SkeletonMixedLoadingMobile,
        }),
        [ContentType.SearchProgram]: Platform.select({
            web: SkeletonMixedLoadingWeb,
            default: isTablet
                ? SkeletonMixedLoadingTablet
                : SkeletonMixedLoadingMobile,
        }),
        [ContentType.SearchCatchup]: Platform.select({
            web: SkeletonMixedLoadingWeb,
            default: isTablet
                ? SkeletonMixedLoadingTablet
                : SkeletonMixedLoadingMobile,
        }),
        [ContentType.NPVR]: Platform.select({
            web: SkeletonMixedLoadingWeb,
            default: isTablet
                ? SkeletonMixedLoadingTablet
                : SkeletonMixedLoadingMobile,
        }),
        [ContentType.ContinueWatching]: Platform.select({
            web: SkeletonMixedLoadingWeb,
            default: isTablet
                ? SkeletonMixedLoadingTablet
                : SkeletonMixedLoadingMobile,
        }),
    };

const CollectionPage = ({
    navigation,
    route: {
        params: {
            railId,
            templateId,
            searchQuery = '',
            searchType = SearchType.PROGRAM,
            searchOptions = {},
            stripeIndex = 0,
        },
    },
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const handleSearchChannels = useChannelsSearch();
    const { screenFocused } = useScreenFocused();

    const modularRail = useSelector(
        selectTemplateviewRailById(templateId, railId),
    );

    const searchRail = useSelector(selectSearchRailById(railId));

    const rail = useMemo(() => {
        return modularRail ?? (searchRail as RBParsedRail | undefined);
    }, [modularRail, searchRail]);

    const railSections = rail?.sections;
    const mdsCall = rail?.properties?.MDS_Call;
    const contentType = rail?.properties?.ContentType;

    const titleRef = useRef(null);

    const { background, primary } = useSelector(selectLayoutConfig);
    const channels = useSelector(selectChannels);
    const { getRailViewParams } = useGetRailViewParams();
    const { getRailSelectionParams } = useGetRailSelectionParams();
    const { getThirdPartyAppStartParams } = useThirdPartyAppStartParams();
    const prevTemplateId = usePrevious(templateId);
    const prevRailId = usePrevious(railId);

    const {
        data,
        totalItems,
        isFetching,
        isErrorOnLoadingNextPage,
        onDiscardErrorOnLoadingNextPage,
        onGetCollectionData,
        onLoadNextPage,
        setFetching,
        errorStatus,
        nextUpdateTime,
    } = useCollectionDataManager({
        rail,
        templateId,
        channels,
        searchQuery,
        searchType,
        searchOptions,
        isCollection: true,
    });
    const debouncedOnLoadNextPage = useCallback(debounce(onLoadNextPage, 300), [
        onLoadNextPage,
    ]);

    useNavigateBackOnHardwareBackPress();
    useAccessibilityFocus({
        accessibleViewRef: titleRef,
        focused: screenFocused,
    });

    const { getDeepLinkTriggeredParams } = useGetDeepLinkTriggeredParams();

    const numColumns = useMemo(() => {
        const windowWidth = Dimensions.get('window').width;
        const listContainer =
            windowWidth - styles.columnWrap.marginHorizontal * 2;
        const calculatedColumns = Math.floor(
            listContainer /
                widthPercentageToDP(
                    contentType === ContentType.ChannelItem ? 176 : 272,
                ),
        );

        if (contentType === ContentType.ChannelItem) {
            return isTablet
                ? 6
                : Platform.select({ web: calculatedColumns, default: 3 });
        }

        return isTablet
            ? 4
            : Platform.select({ web: calculatedColumns, default: 2 });
    }, [contentType]);

    const items = useMemo(() => {
        let list;

        if (searchQuery && contentType === ContentType.ChannelItem) {
            list = handleSearchChannels(searchQuery) as CollectionItemsList;
        } else if (mdsCall && mdsCall === MdsCallKeys.CHANNELS) {
            list = channels;
        } else if (
            mdsCall &&
            mdsCall !== MdsCallKeys.SEARCH &&
            mdsCall !== MdsCallKeys.RECORDINGS &&
            mdsCall !== MdsCallKeys.NOW_NEXT
        ) {
            list = (railSections || []) as CollectionItemsList;
        } else if (!mdsCall && contentType === ContentType.ChannelItem) {
            list = parseKidsChannels(
                channels,
                data as EventItemWithServiceInfo[],
            );
        } else {
            list = data;
        }

        return prepareCollectionList(numColumns, list, contentType);
    }, [
        data,
        searchQuery,
        contentType,
        mdsCall,
        numColumns,
        handleSearchChannels,
        railSections,
        channels,
    ]);

    const { visibleItems, viewabilityConfig, onViewableItemsChanged } =
        useViewableItems(items, contentType, true);

    const [refreshing, setRefreshing] = useState(false);

    const totalListLength = useMemo(() => {
        return (
            totalItems ||
            items?.filter(
                // @ts-ignore
                (
                    item:
                        | MixedItem
                        | RecordingItem
                        | RBChannel
                        | NowNextData
                        | SearchProgram
                        | EmptyCollectionItem,
                ) => !isEmptyItem(item),
            ).length
        );
    }, [totalItems, items]);

    const handleGetCollectionData = useCallback(async () => {
        if (
            !mdsCall ||
            mdsCall === MdsCallKeys.SEARCH ||
            mdsCall === MdsCallKeys.RECORDINGS ||
            mdsCall === MdsCallKeys.NOW_NEXT
        ) {
            await onGetCollectionData();
        } else {
            setFetching(false);
        }
    }, [mdsCall, onGetCollectionData]);

    const onRailsSelection = useCallback(
        ({
            id,
            playbackType,
            index,
        }: {
            id: string;
            playbackType: StreamTypes;
            index: number;
        }) => {
            if (railId && templateId) {
                logUavAnalytics(
                    getRailSelectionParams(
                        templateId,
                        railId,
                        index,
                        id,
                        playbackType,
                    ),
                );
            }
        },
        [getRailSelectionParams, templateId, railId],
    );

    const onThirdPartyAppStart = useCallback(
        ({
            appReference,
            deeplinkProviderId,
            deepLinkid,
            providerResourceId,
            appName,
        }: ThirdPartyAppStartItem) => {
            logUavAnalytics(
                getThirdPartyAppStartParams({
                    templateId,
                    railId,
                    appReference,
                    deeplinkProviderId,
                    deepLinkid,
                    providerResourceId,
                    appName,
                    playbackType: undefined,
                }),
            );
        },
        [templateId, railId],
    );

    const onDeepLinkTriggered = useCallback(
        (item: DeepLinkTriggered) => {
            if (templateId) {
                logUavAnalytics(
                    getDeepLinkTriggeredParams({
                        templateId,
                        railId,
                        ...item,
                    }),
                );
            }
        },
        [templateId, railId, getDeepLinkTriggeredParams],
    );

    const renderItem = useCallback(
        ({
            item,
            index,
        }: ListRenderItemInfo<
            NowNextData | RBChannel | RecordingItem | EmptyCollectionItem
        >) => {
            const emptyItem = item as EmptyCollectionItem;
            const contentItem = item as NowNextData | RBChannel;

            if (isEmptyItem(emptyItem)) {
                return (
                    <View style={emptyItemStyleByContentType[emptyItem.type]} />
                );
            }

            return (
                <RibbonItem
                    item={contentItem}
                    index={index}
                    metadataType={contentType || ContentType.Catchup}
                    visibleItems={visibleItems}
                    isCollection
                    onRailsSelection={onRailsSelection}
                    templateId={templateId}
                    railId={railId}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    onThirdPartyAppStart={onThirdPartyAppStart}
                    totalNumberOfItems={totalListLength}
                />
            );
        },
        [
            contentType,
            onRailsSelection,
            onDeepLinkTriggered,
            onThirdPartyAppStart,
            templateId,
            railId,
            totalListLength,
        ],
    );

    const onRefresh = useCallback(async () => {
        setRefreshing(true);

        if (!mdsCall || mdsCall !== MdsCallKeys.SEARCH) {
            await onGetCollectionData();
        }

        setRefreshing(false);
    }, [mdsCall, onGetCollectionData]);

    const handleTryAgainPressedOnDialog = useCallback(() => {
        onDiscardErrorOnLoadingNextPage();
        onLoadNextPage();
    }, [onDiscardErrorOnLoadingNextPage, onLoadNextPage]);

    const handleCancelPressedOnDialog = useCallback(() => {
        onDiscardErrorOnLoadingNextPage();
    }, [onDiscardErrorOnLoadingNextPage]);

    useHeaderConfig({
        isRoot: isWeb,
        needsClose: false,
        headerTransparent: false,
    });

    useEffect(() => {
        dispatch(
            endSentryTransaction(SENTRY_TRANSACTIONS.CollectionPageRendering),
        );
    }, []);

    useEffect(() => {
        if (
            templateId &&
            railId &&
            templateId !== prevTemplateId &&
            railId !== prevRailId
        ) {
            logUavAnalytics(getRailViewParams(templateId, railId, stripeIndex));
        }
    }, [templateId, railId, getRailViewParams, stripeIndex]);

    useFocusEffect(
        useCallback(() => {
            if (isPhone || isTablet) {
                navigation.setParams({
                    totalItems: isFetching ? undefined : totalListLength,
                    shouldSetAccessibilityFocus: !isFetching,
                });
            }
        }, [navigation, totalListLength, isFetching]),
    );

    useFocusEffect(
        useCallback(() => {
            handleGetCollectionData();

            return () => {
                if (!mdsCall || mdsCall === MdsCallKeys.SEARCH) {
                    setFetching(true);
                }
            };
        }, []),
    );

    const interval = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (
            contentType === ContentType.NowNext &&
            screenFocused &&
            interval.current === null &&
            nextUpdateTime
        ) {
            interval.current = setInterval(() => {
                if (Date.now() >= nextUpdateTime) {
                    onGetCollectionData();
                }
            }, 5000);
        }

        return () => {
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = null;
            }
        };
    }, [screenFocused, nextUpdateTime]);

    return (
        <View style={[gStyles.flex, { backgroundColor: background }]}>
            {isWeb &&
                ((rail?.properties.ContentType === ContentType.VOD &&
                    totalListLength !== 0) ||
                    rail?.properties.ContentType !== ContentType.VOD) && (
                    <View
                        ref={titleRef}
                        style={styles.titleContainer}
                        testID={`COLLECTION_TITLE_${rail?.title}_ID`}
                        accessibilityLabel={t(rail?.title || rail?.name || '')}
                        tabIndex={-1}
                    >
                        {(rail?.title || rail?.name) && (
                            <Text
                                style={styles.titleText}
                                ref={titleRef}
                                accessibilityLabel={t(
                                    rail?.title || rail?.name,
                                )}
                                // @ts-ignore
                                tabIndex={isFetching ? -1 : 0}
                                aria-hidden
                            >
                                {t(rail?.title || rail?.name)}
                            </Text>
                        )}
                        <Text
                            style={styles.numberText}
                            // @ts-ignore
                            tabIndex={0}
                        >
                            {isFetching ? undefined : totalListLength ?? ''}
                        </Text>
                    </View>
                )}

            {rail && !isFetching && (
                <FlatList
                    refreshControl={
                        mdsCall !== MdsCallKeys.SEARCH ? (
                            <RefreshControl
                                refreshing={refreshing}
                                onRefresh={onRefresh}
                            />
                        ) : undefined
                    }
                    viewabilityConfig={viewabilityConfig}
                    columnWrapperStyle={styles.columnWrap}
                    style={[
                        gStyles.flex,
                        styles.list,
                        { backgroundColor: background },
                    ]}
                    contentContainerStyle={[
                        styles.listContentContainer,
                        contentType && listContainerStyle[contentType],
                    ]}
                    numColumns={numColumns}
                    extraData={visibleItems}
                    // @ts-ignore
                    data={items}
                    // @ts-ignore
                    keyExtractor={keyExtractor}
                    renderItem={renderItem}
                    removeClippedSubviews={!isWeb}
                    ListEmptyComponent={
                        <View style={styles.noContentContainer}>
                            <Text style={styles.noContentText}>
                                {t('SCREEN_COLLECTION_EMPTY_CONTENT')}
                            </Text>
                        </View>
                    }
                    initialNumToRender={12}
                    maxToRenderPerBatch={12}
                    windowSize={5}
                    showsVerticalScroll
                    Indicator={false}
                    onEndReachedThreshold={0.5}
                    onEndReached={debouncedOnLoadNextPage}
                    onViewableItemsChanged={onViewableItemsChanged}
                />
            )}
            {isFetching && !refreshing && !items.length && (
                <SkeletonLoading
                    isScreenSkeleton
                    SkeletonLoadingComponent={
                        SkeletonToMetadataType[
                            contentType && SkeletonToMetadataType[contentType]
                                ? contentType
                                : ContentType.Catchup
                        ] as React.FC<SvgProps>
                    }
                    backgroundColor={background}
                    animatedBlockStyle={styles.skeletonContainer}
                    svgStyle={styles.skeletonSvgContainer}
                />
            )}

            {isTablet ? (
                <ErrorDialog
                    visible={isErrorOnLoadingNextPage}
                    title={DIALOG_ERR_GENERIC_TITLE}
                    description={DIALOG_ERR_GENERIC_BODY}
                    actionButtonTitle={DIALOG_TRY_AGAIN_BUTTON}
                    actionButtonAccessibilityLabel={t(DIALOG_TRY_AGAIN_BUTTON)}
                    actionButtonOnPress={handleTryAgainPressedOnDialog}
                    actionButtonFillColor={primary}
                    cancelButtonTitle={DIALOG_CANCEL_BUTTON}
                    cancelButtonAccessibilityLabel={t(DIALOG_CANCEL_BUTTON)}
                    cancelButtonBordered
                    cancelButtonOnPress={handleCancelPressedOnDialog}
                    errorStatus={errorStatus}
                />
            ) : (
                <ErrorDialog
                    visible={isErrorOnLoadingNextPage}
                    title={DIALOG_ERR_GENERIC_TITLE}
                    description={DIALOG_ERR_GENERIC_BODY}
                    actionButtonTitle={DIALOG_CANCEL_BUTTON}
                    actionButtonAccessibilityLabel={t(DIALOG_CANCEL_BUTTON)}
                    actionButtonOnPress={handleCancelPressedOnDialog}
                    actionButtonBordered
                    cancelButtonTitle={DIALOG_TRY_AGAIN_BUTTON}
                    cancelButtonAccessibilityLabel={t(DIALOG_TRY_AGAIN_BUTTON)}
                    cancelButtonFillColor={primary}
                    cancelButtonOnPress={handleTryAgainPressedOnDialog}
                    errorStatus={errorStatus}
                />
            )}
        </View>
    );
};

export default CollectionPage;

function keyExtractor(item: ColumnsCollectionItemsList[number]) {
    if ((item as RBChannel)?.editorial?.id) {
        return (item as RBChannel).editorial.id;
    }

    if ((item as NowNextData)?.channelId && (item as NowNextData)?.programmes) {
        return (item as NowNextData)?.channelId;
    }

    return (
        item as
            | RecordingItem
            | MixedItem
            | RBProgram
            | SearchProgram
            | EmptyCollectionItem
    )?.id;
}
